/**
 * Activity Log — chronological list of company configuration changes.
 *
 * Reused in two places:
 *   - Company settings → "Activity log" tab            (mode='company')
 *   - Admin → company drawer "Activity log" section    (mode='admin', companyId=X)
 *
 * Each row shows: when · category badge · summary · who · details toggle.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, History, ChevronDown, ChevronRight, Shield, Wallet, DollarSign, Building2, Settings as SettingsIcon } from "lucide-react";

const CATEGORY_META = {
  marketplace:  { label: "Marketplace",  icon: Shield,      tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  auto_reload:  { label: "Auto-reload",  icon: Wallet,      tone: "bg-blue-50 text-blue-700 border-blue-200" },
  pricing:      { label: "Pricing",      icon: DollarSign,  tone: "bg-amber-50 text-amber-700 border-amber-200" },
  business:     { label: "Business",     icon: Building2,   tone: "bg-slate-50 text-slate-700 border-slate-200" },
  default:      { label: "Settings",     icon: SettingsIcon, tone: "bg-slate-50 text-slate-700 border-slate-200" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AuditLog({ mode = "company", companyId = null }) {
  const [state, setState] = useState({ loading: true, entries: [], company: null });
  const [openIds, setOpenIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    const url = mode === "admin" ? `/admin/audit/${companyId}` : `/audit/me`;
    api.get(url)
      .then((r) => { if (!cancelled) setState({ loading: false, entries: r.data?.entries || [], company: r.data?.company || null }); })
      .catch(() => { if (!cancelled) setState({ loading: false, entries: [], company: null }); });
    return () => { cancelled = true; };
  }, [mode, companyId]);

  const toggle = (id) => {
    setOpenIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  if (state.loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 inline-flex items-center gap-2 text-slate-500 text-sm" data-testid="audit-log-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading activity…
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="audit-log-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
          <History className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Activity log</h3>
          <p className="text-sm text-slate-600 mt-0.5">
            {mode === "admin"
              ? "Every change this company has made to settings affecting lead delivery."
              : "A timeline of changes you've made — handy for support and dispute reviews."}
          </p>
        </div>
      </div>

      {state.entries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center text-sm text-slate-500" data-testid="audit-log-empty">
          No activity yet. Changes you make to marketplace, pricing, or auto-reload settings will appear here.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="audit-log-list">
          {state.entries.map((e) => {
            const meta = CATEGORY_META[e.category] || CATEGORY_META.default;
            const Icon = meta.icon;
            const isOpen = openIds.has(e.id);
            const hasChanges = Array.isArray(e.changes) && e.changes.length > 0;
            return (
              <li key={e.id} className="rounded-xl border border-slate-200 hover:border-slate-300 transition" data-testid={`audit-entry-${e.category}`}>
                <button
                  type="button"
                  onClick={() => hasChanges && toggle(e.id)}
                  className={`w-full text-left p-3 sm:p-4 flex items-start gap-3 ${hasChanges ? "cursor-pointer" : "cursor-default"}`}
                >
                  <div className={`mt-0.5 shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 leading-snug">{e.summary || "Setting changed"}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {fmtTime(e.created_at)}
                      {e.actor_email && <> · <span className="text-slate-700">{e.actor_email}</span></>}
                      {e.actor_role === "platform_admin" && <span className="ml-1 text-[10px] uppercase tracking-wide text-rose-600 font-bold">Admin</span>}
                    </div>
                  </div>
                  {hasChanges && (
                    <div className="shrink-0 text-slate-400 mt-0.5">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                  )}
                </button>
                {isOpen && hasChanges && (
                  <div className="border-t border-slate-100 px-3 sm:px-4 py-3 bg-slate-50/40 text-xs">
                    <table className="w-full" data-testid={`audit-entry-${e.id}-diff`}>
                      <thead>
                        <tr className="text-slate-500 text-[10px] uppercase tracking-wider">
                          <th className="text-left py-1 pr-2 w-1/3">Field</th>
                          <th className="text-left py-1 pr-2">Before</th>
                          <th className="text-left py-1">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {e.changes.map((d, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="py-1.5 pr-2 font-mono text-slate-700">{d.field}</td>
                            <td className="py-1.5 pr-2 font-mono text-rose-700 break-all">{summarize(d.before)}</td>
                            <td className="py-1.5 font-mono text-emerald-700 break-all">{summarize(d.after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function summarize(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "on" : "off";
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]";
    if (v.length <= 4) return v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(", ");
    return `${v.length} items`;
  }
  if (typeof v === "object") {
    try { return JSON.stringify(v).slice(0, 80); } catch { return "[object]"; }
  }
  return String(v);
}
